import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  HistoryOutlined,
  MinusCircleTwoTone,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import { qaApi } from "services/qaApi";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import IconButton from "components/common/IconButton";
import { STORAGE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";
import "./qa-tests.css";

const { Text } = Typography;
const { TextArea } = Input;

const DEFAULT_SUB_FEATURES = ["View", "Create", "Edit", "Delete"];
const SUB_FEATURE_NOTE_PREFIX = "[Sub-Feature:";
const MAX_ATTACHMENTS_PER_RECORD = 10;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const SUB_FEATURES_PREVIEW_COUNT = 5;
const MODAL_Z_INDEX = 100500;
const FEATURES_FETCH_SIZE = 500;
const SORT_MODES = [
  { label: "Stable (no auto resort)", value: "STABLE" },
  { label: "Recently tested", value: "RECENT" },
];

const PRIORITY_OPTIONS = [
  { label: "Critical", value: "CRITICAL" },
  { label: "High", value: "HIGH" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Low", value: "LOW" },
];

const STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Archived", value: "ARCHIVED" },
];

const COMBINED_FILTER_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Archived", value: "ARCHIVED" },
  { label: "Last tested", value: "LAST_TESTED" },
];
const statusIcons = {
  PASS: <CheckCircleTwoTone twoToneColor="#52c41a" />,
  FAIL: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
  NONE: <MinusCircleTwoTone twoToneColor="#bfbfbf" />,
};

const statusTags = {
  PASS: <Tag color="success">PASS</Tag>,
  FAIL: <Tag color="error">FAIL</Tag>,
  NONE: <Tag color="default">Not Set</Tag>,
};

const getFeatureStatusColor = (status = "") => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ACTIVE") return "green";
  if (normalized === "INACTIVE") return "orange";
  if (normalized === "ARCHIVED") return "default";
  return "blue";
};

const computeTestCounts = (feature, results = {}) => {
  const subFeatures = Array.isArray(feature?.subFeatures)
    ? feature.subFeatures
    : DEFAULT_SUB_FEATURES;

  const counts = { passes: 0, fails: 0, untested: 0 };

  subFeatures.forEach((subFeature) => {
    const status = String(results?.[subFeature]?.result || "NONE").toUpperCase();
    if (status === "PASS") counts.passes += 1;
    else if (status === "FAIL") counts.fails += 1;
    else counts.untested += 1;
  });

  return counts;
};

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".bmp",
  ".svg",
];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const normalizeAttachment = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === "string") {
    const trimmed = attachment.trim();
    if (!trimmed) return null;
    return {
      fileId: trimmed,
      url: trimmed,
      name: trimmed,
    };
  }

  return attachment;
};

const getAttachmentFileId = (attachment = {}) =>
  attachment.fileId ||
  attachment.fid ||
  attachment.file_id ||
  attachment.id ||
  attachment.file?.fileId ||
  attachment.file?.id ||
  null;

const getAttachmentName = (attachment = {}) =>
  attachment.name ||
  attachment.fileName ||
  attachment.filename ||
  attachment.originalName ||
  attachment.key ||
  getAttachmentFileId(attachment) ||
  "Attachment";

const getAttachmentUrl = (attachment = {}) =>
  attachment.url ||
  attachment.uri ||
  attachment.fileUrl ||
  attachment.downloadUrl ||
  attachment.publicUrl ||
  attachment.signedUrl ||
  attachment.file?.url ||
  attachment.file?.uri ||
  attachment.file?.fileUrl ||
  attachment.file?.downloadUrl ||
  attachment.file?.publicUrl ||
  attachment.file?.signedUrl ||
  "";

const normalizeAttachments = (value) =>
  toArray(value)
    .map(normalizeAttachment)
    .filter(Boolean)
    .map((attachment) => {
      const fileId = getAttachmentFileId(attachment);
      const url = getAttachmentUrl(attachment);
      const name = getAttachmentName(attachment);

      return {
        ...attachment,
        fileId: attachment.fileId || fileId,
        id: attachment.id || fileId,
        name,
        url,
      };
    });

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMMM D, YYYY h:mm A") : "-";
};

const parseSubFeatures = (rawValue) => {
  const values = String(rawValue || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return values.length ? values : DEFAULT_SUB_FEATURES;
};

const extractRawSubFeatures = (feature) =>
  feature?.subFeatures ??
  feature?.subfeatures ??
  feature?.testCases ??
  feature?.testcases ??
  feature?.sub_features ??
  null;

const normalizeSubFeatures = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return DEFAULT_SUB_FEATURES;

  if (Array.isArray(rawValue)) {
    return rawValue.map((entry) => String(entry || "").trim()).filter(Boolean);
  }

  if (typeof rawValue === "string") {
    return parseSubFeatures(rawValue);
  }

  if (rawValue && typeof rawValue === "object") {
    return toArray(rawValue)
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
  }

  return DEFAULT_SUB_FEATURES;
};

const buildFeaturePayload = (values) => ({
  name: values.featureName?.trim(),
  description: values.description?.trim() || "",
  category: values.category?.trim() || "General",
  priority: values.priority || "MEDIUM",
  status: values.status || "ACTIVE",
  subFeatures:
    Array.isArray(values.subFeatures) && values.subFeatures.length
      ? values.subFeatures
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
      : parseSubFeatures(values.subFeaturesText),
  attachments: values.attachments || [],
});

const buildResultPayload = (subFeature, values) => ({
  subFeature,
  result: values.result,
  testDate: values.testDate
    ? dayjs(values.testDate).toISOString()
    : new Date().toISOString(),
  notes: values.notes?.trim() || "",
});

const buildRunNotes = (subFeature, notes) => {
  const trimmedNotes = notes?.trim() || "";
  return `${SUB_FEATURE_NOTE_PREFIX} ${subFeature}]${
    trimmedNotes ? ` ${trimmedNotes}` : ""
  }`;
};

const parseRunNotes = (notes) => {
  const rawNotes = String(notes || "").trim();
  if (!rawNotes.startsWith(SUB_FEATURE_NOTE_PREFIX)) {
    return { subFeature: null, notes: rawNotes };
  }

  const closingIndex = rawNotes.indexOf("]");
  if (closingIndex === -1) {
    return { subFeature: null, notes: rawNotes };
  }

  const subFeature = rawNotes
    .slice(SUB_FEATURE_NOTE_PREFIX.length, closingIndex)
    .trim();
  const cleanNotes = rawNotes.slice(closingIndex + 1).trim();

  return { subFeature: subFeature || null, notes: cleanNotes };
};

const isImageAttachment = (attachment = {}) => {
  const value = String(attachment.name || attachment.url || "").toLowerCase();
  const mimeType = String(
    attachment.mimeType || attachment.contentType || attachment.type || "",
  ).toLowerCase();
  if (mimeType.startsWith("image/")) return true;
  return IMAGE_EXTENSIONS.some((extension) => value.includes(extension));
};

const resolveSignedUrlPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (Array.isArray(payload) && payload.length > 0) return payload[0];
  if (Array.isArray(payload?.signedUrls) && payload.signedUrls.length > 0) {
    return payload.signedUrls[0];
  }
  if (typeof payload?.url === "string" && payload.url.trim()) return payload.url;
  return null;
};

const parseHistoryItems = (data) => {
  const source =
    data?.content ||
    data?.runs ||
    data?.history ||
    data?.items ||
    data?.results ||
    [];

  return toArray(source).map((entry, index) => {
    const parsedNotes = parseRunNotes(entry.notes);

    return {
      id: entry.id || `history-${index}-${entry.testDate || ""}`,
      result: String(entry.result || "-").toUpperCase(),
      testDate: entry.testDate,
      notes: parsedNotes.notes,
      subFeature: entry.subFeature || parsedNotes.subFeature,
      testedBy: entry.testedBy,
      versionBuild: entry.versionBuild,
      attachments: normalizeAttachments(
        entry.attachments ||
          entry.files ||
          entry.fileAttachments ||
          entry.evidence ||
          entry.documents ||
          entry.run?.attachments ||
          entry.run?.files ||
          entry.run?.documents ||
          entry.run?.evidence ||
          entry.runAttachments ||
          entry.runFiles ||
          [],
      ),
    };
  });
};

const normalizeSubFeatureValue = (value) =>
  String(value || "").trim().toLowerCase();

export default function QaTestFeaturesPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { uploadFile, getFileMetadata, uploading } = useFileUpload();

  const restoreState = location?.state?.restore || null;

  const seededApp =
    location?.state?.app && String(location.state.app.id) === String(appId)
      ? location.state.app
      : null;

  const [app, setApp] = useState(seededApp);
  const [appLoading, setAppLoading] = useState(!seededApp);
  const [appError, setAppError] = useState(false);

  const [features, setFeatures] = useState([]);
  const [featureResults, setFeatureResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(() => restoreState?.searchText || "");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => restoreState?.statusFilter || "ALL");
  const [resultFilter, setResultFilter] = useState(() => restoreState?.resultFilter || "ALL");
  const [sortMode, setSortMode] = useState(() => restoreState?.sortMode || "STABLE");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => (Number.isFinite(restoreState?.page) ? restoreState.page : 0));
  const [pageSize, setPageSize] = useState(() => (Number.isFinite(restoreState?.pageSize) ? restoreState.pageSize : 20));
  const [casePage, setCasePage] = useState(() => (Number.isFinite(restoreState?.casePage) ? restoreState.casePage : 0));
  const [casePageSize, setCasePageSize] = useState(() => (Number.isFinite(restoreState?.casePageSize) ? restoreState.casePageSize : 20));

  const [testerNameById, setTesterNameById] = useState({});
  const [editingCellKey, setEditingCellKey] = useState(null);
  const [editingCellValue, setEditingCellValue] = useState("");
  const [editingFeatureId, setEditingFeatureId] = useState(null);
  const [editingFeatureValue, setEditingFeatureValue] = useState("");
  const [activeRowKey, setActiveRowKey] = useState(null);
  const [savingCellKey, setSavingCellKey] = useState(null);
  const [savingFeatureId, setSavingFeatureId] = useState(null);

  const [testCasesDrawerOpen, setTestCasesDrawerOpen] = useState(false);
  const [testCasesDrawerFeature, setTestCasesDrawerFeature] = useState(null);
  const [testCasesSearch, setTestCasesSearch] = useState("");
  const [testCasesResultFilter, setTestCasesResultFilter] = useState("ALL");
  const [testCasesPagination, setTestCasesPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [featureDrawerOpen, setFeatureDrawerOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [featureSubmitting, setFeatureSubmitting] = useState(false);
  const [featureAttachments, setFeatureAttachments] = useState([]);

  const [resultDrawerOpen, setResultDrawerOpen] = useState(false);
  const [resultSubmitting, setResultSubmitting] = useState(false);
  const [editingResultContext, setEditingResultContext] = useState(null);
  const [runAttachments, setRunAttachments] = useState([]);
  const [prefillAttachmentsLoading, setPrefillAttachmentsLoading] = useState(false);

  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailContext, setDetailContext] = useState(null);

  const [attachmentPreviewOpen, setAttachmentPreviewOpen] = useState(false);
  const [attachmentPreviewTitle, setAttachmentPreviewTitle] = useState("");
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState("");
  const [attachmentPreviewIsImage, setAttachmentPreviewIsImage] = useState(false);

  const [featureForm] = Form.useForm();
  const [resultForm] = Form.useForm();
  const [addTestCaseForm] = Form.useForm();
  const watchedResult = Form.useWatch("result", resultForm);
  const restoredAtRef = useRef(Boolean(restoreState) ? Date.now() : null);

  const fetchSignedUrlForFileId = async (fileId) => {
    if (!fileId) return null;

    try {
      const response = await apiClient.get(
        `/storage-service/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(
          fileId,
        )}`,
      );
      const signedUrl = resolveSignedUrlPayload(response?.data);
      if (signedUrl) return signedUrl;
    } catch {
      // ignore
    }

    try {
      const metadata = await getFileMetadata(fileId);
      return (
        metadata?.publicUrl ||
        metadata?.url ||
        metadata?.fileUrl ||
        metadata?.downloadUrl ||
        metadata?.signedUrl ||
        metadata?.uri ||
        null
      );
    } catch {
      return null;
    }
  };

  const fetchApp = async ({ silent = false } = {}) => {
    if (!silent) setAppLoading(true);
    setAppError(false);
    try {
      const data = await qaApi.apps.get(appId);
      setApp(data);
    } catch (err) {
      console.error(err);
      setAppError(true);
    } finally {
      if (!silent) setAppLoading(false);
    }
  };

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const listAll = [];
      let totalElements = null;
      let nextPage = 0;

      // The main table paginates client-side, so we must load the full dataset here.
      // Otherwise records "disappear" once the server-side page boundary is crossed.
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const data = await qaApi.features.list(appId, {
          page: nextPage,
          size: FEATURES_FETCH_SIZE,
          status: statusFilter !== "ALL" ? statusFilter : undefined,
          search: debouncedSearchText ? debouncedSearchText : undefined,
        });

        if (totalElements === null) {
          const parsedTotal = Number(data?.totalElements);
          totalElements = Number.isFinite(parsedTotal) ? parsedTotal : null;
        }

        const pageItems = data?.content || data?.features || [];
        if (!Array.isArray(pageItems) || pageItems.length === 0) break;

        listAll.push(...pageItems);
        nextPage += 1;

        if (totalElements !== null && listAll.length >= totalElements) break;

        // Safety valve: don't loop forever if API shape is unexpected.
        if (nextPage > 200) break;
      }

      const list = listAll.map((feature) => ({
        ...feature,
        subFeatures: normalizeSubFeatures(extractRawSubFeatures(feature)),
      }));
      setFeatures(list);
      setTotal(totalElements ?? list.length ?? 0);

      const resultsEntries = await Promise.all(
        list.map(async (feature) => {
          try {
            const resultsResponse = await qaApi.features.getResults(
              appId,
              feature.id,
            );
            return [feature.id, resultsResponse?.results || {}];
          } catch {
            return [feature.id, {}];
          }
        }),
      );

      setFeatureResults(Object.fromEntries(resultsEntries));
      return list;
    } catch (err) {
      message.error("Failed to load features");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appId) {
      fetchApp({ silent: Boolean(seededApp) });
    }
  }, [appId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      // Only reset pagination when the user changes the search text.
      // When returning from History pages we restore the previous page from navigation state.
      if (!restoreState) setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (appId) {
      fetchFeatures();
    }
  }, [appId, statusFilter, debouncedSearchText]);

  const filteredFeatures = useMemo(() => {
    const normalizedStatus = String(statusFilter || "ALL").toUpperCase();
    const searchTerm = searchText.trim().toLowerCase();

    const base = features.filter((feature) => {
      if (normalizedStatus !== "ALL") {
        const featureStatus = String(feature.status || "").toUpperCase();
        if (featureStatus !== normalizedStatus) return false;
      }

      if (!searchTerm) return true;
      return (
        feature.name?.toLowerCase().includes(searchTerm) ||
        feature.description?.toLowerCase().includes(searchTerm)
      );
    });

    // Start from API order for stable mode (keeps user context after saves).
    let sorted = [...base];

    const getCreatedAt = (feature) => {
      const value =
        feature?.createdAt ||
        feature?.created_at ||
        feature?.dateCreated ||
        feature?.createdOn ||
        feature?.createdDate ||
        feature?.updatedAt ||
        feature?.updated_at ||
        null;
      const parsed = value ? dayjs(value) : null;
      return parsed && parsed.isValid() ? parsed.valueOf() : 0;
    };

    const getLastTestedAt = (featureId) => {
      const results = featureResults?.[featureId] || {};
      return Object.values(results).reduce((max, entry) => {
        const parsed = entry?.testDate ? dayjs(entry.testDate) : null;
        const ts = parsed && parsed.isValid() ? parsed.valueOf() : 0;
        return Math.max(max, ts);
      }, 0);
    };

    if (String(sortMode || "STABLE").toUpperCase() === "RECENT") {
      // Optional ordering: most recently tested first, then most recently added/updated.
      sorted.sort((a, b) => {
        const testedDiff = getLastTestedAt(b.id) - getLastTestedAt(a.id);
        if (testedDiff !== 0) return testedDiff;
        return getCreatedAt(b) - getCreatedAt(a);
      });
    }

    return sorted;
  }, [features, searchText, statusFilter, featureResults, sortMode]);

  const testCaseRows = useMemo(() => {
    const searchTerm = searchText.trim().toLowerCase();
    const normalizedResult = String(resultFilter || "ALL").toUpperCase();

    const rows = [];

    filteredFeatures.forEach((feature) => {
      const baseSubFeatures = Array.isArray(feature?.subFeatures)
        ? feature.subFeatures
        : DEFAULT_SUB_FEATURES;

      const normalizedSortMode = String(sortMode || "STABLE").toUpperCase();
      const subFeatures =
        normalizedSortMode === "RECENT"
          ? // Keep most-recently tested sub-features at the top within a feature.
            [...baseSubFeatures].sort((a, b) => {
              const aData = featureResults?.[feature.id]?.[a] || null;
              const bData = featureResults?.[feature.id]?.[b] || null;
              const aTs =
                aData?.testDate && dayjs(aData.testDate).isValid()
                  ? dayjs(aData.testDate).valueOf()
                  : 0;
              const bTs =
                bData?.testDate && dayjs(bData.testDate).isValid()
                  ? dayjs(bData.testDate).valueOf()
                  : 0;
              return bTs - aTs;
            })
          : baseSubFeatures;

      subFeatures.forEach((subFeature) => {
        const data = featureResults?.[feature.id]?.[subFeature] || null;
        const result = String(data?.result || "NONE").toUpperCase();

        if (normalizedResult !== "ALL" && result !== normalizedResult) return;

        if (searchTerm) {
          const featureName = String(feature?.name || "").toLowerCase();
          const sf = String(subFeature || "").toLowerCase();
          if (!featureName.includes(searchTerm) && !sf.includes(searchTerm)) return;
        }

        rows.push({
          key: `${feature.id}-${subFeature}`,
          feature,
          subFeature,
          result,
          testDate: data?.testDate || null,
          testedBy: data?.testedBy || null,
        });
      });
    });

    return rows;
  }, [filteredFeatures, featureResults, resultFilter, searchText, sortMode]);

  const pagedTestCaseRows = useMemo(() => {
    const all = Array.isArray(testCaseRows) ? testCaseRows : [];
    const start = casePage * casePageSize;
    return all.slice(start, start + casePageSize);
  }, [casePage, casePageSize, testCaseRows]);

  const resolveTesterName = useCallback(
    (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "-";
      if (raw === user?.id) {
        const selfName =
          user?.fullName ||
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
          user?.email ||
          user?.username ||
          raw;
        return selfName || raw;
      }
      if (testerNameById?.[raw]) return testerNameById[raw];
      // If backend already returns a readable name/email, show it as-is.
      if (raw.includes(" ") || raw.includes("@")) return raw;
      return raw;
    },
    [testerNameById, user],
  );

  useEffect(() => {
    const uuidLike =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const ids = Array.from(
      new Set(
        (Array.isArray(testCaseRows) ? testCaseRows : [])
          .map((row) => String(row?.testedBy || "").trim())
          .filter(Boolean)
          .filter((id) => uuidLike.test(id) || /^[0-9]+$/.test(id)),
      ),
    ).filter((id) => testerNameById[id] === undefined);

    if (!ids.length) return;
    // Avoid flooding identity-service on huge lists.
    const toFetch = ids.slice(0, 50);
    let cancelled = false;

    const run = async () => {
      const results = await Promise.allSettled(
        toFetch.map(async (id) => {
          try {
            const res = await apiClient.get(
              `/identity-service/api/v1/users/${encodeURIComponent(id)}`,
            );
            const data = res?.data || {};
            const name =
              data.fullName ||
              `${data.firstName || ""} ${data.lastName || ""}`.trim() ||
              data.name ||
              data.username ||
              data.email ||
              id;
            return [id, name || id];
          } catch {
            return [id, id];
          }
        }),
      );

      if (cancelled) return;
      const entries = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);
      if (!entries.length) return;
      setTesterNameById((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [testCaseRows, testerNameById]);

  const featureRowSpanByKey = useMemo(() => {
    const rows = Array.isArray(pagedTestCaseRows) ? pagedTestCaseRows : [];
    const map = new Map();
    let i = 0;
    while (i < rows.length) {
      const featureId = rows[i]?.feature?.id || null;
      let j = i;
      while (j < rows.length && (rows[j]?.feature?.id || null) === featureId) j += 1;
      map.set(rows[i].key, j - i);
      for (let k = i + 1; k < j; k += 1) map.set(rows[k].key, 0);
      i = j;
    }
    return map;
  }, [pagedTestCaseRows]);

  useEffect(() => {

    if (restoredAtRef.current && Date.now() - restoredAtRef.current < 2000) {
      return;
    }
    setCasePage(0);
  }, [searchText, statusFilter, resultFilter, sortMode]);

  useEffect(() => {
    if (!restoreState) return;
    // Clear restore state after first render so normal interactions behave as expected.
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...(location.state || {}), restore: null },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const featureCountsById = useMemo(() => {
    const entries = features.map((feature) => [
      feature.id,
      computeTestCounts(feature, featureResults[feature.id] || {}),
    ]);
    return Object.fromEntries(entries);
  }, [features, featureResults]);

  const buildFeatureUpdatePayloadFromFeature = useCallback(
    (feature, overrides = {}) => {
      const subFeatures =
        overrides.subFeatures ||
        (Array.isArray(feature?.subFeatures)
          ? feature.subFeatures
          : normalizeSubFeatures(extractRawSubFeatures(feature)));

      const attachments = normalizeAttachments(
        overrides.attachments ?? feature?.attachments ?? [],
      )
        .map((attachment) => ({
          fileId: getAttachmentFileId(attachment),
          name: getAttachmentName(attachment),
          size: attachment.size,
        }))
        .filter((attachment) => Boolean(attachment.fileId));

      return {
        name: overrides.name ?? feature?.name ?? "",
        description: overrides.description ?? feature?.description ?? "",
        category: overrides.category ?? feature?.category ?? "General",
        priority: overrides.priority ?? feature?.priority ?? "MEDIUM",
        status: overrides.status ?? feature?.status ?? "ACTIVE",
        subFeatures,
        attachments,
      };
    },
    [],
  );

  const renameSubFeature = useCallback(
    async (feature, fromValue, toValue) => {
      const from = String(fromValue || "").trim();
      const to = String(toValue || "").trim();
      if (!feature?.id || !from) return;
      if (!to) {
        message.warning("Test case name cannot be empty.");
        return;
      }

      const current = Array.isArray(feature?.subFeatures)
        ? feature.subFeatures
        : normalizeSubFeatures(extractRawSubFeatures(feature));

      const duplicate = current.some(
        (entry) => normalizeSubFeatureValue(entry) === normalizeSubFeatureValue(to),
      );
      if (duplicate && normalizeSubFeatureValue(from) !== normalizeSubFeatureValue(to)) {
        message.warning("A test case with that name already exists for this feature.");
        return;
      }

      const nextSubFeatures = current.map((entry) =>
        normalizeSubFeatureValue(entry) === normalizeSubFeatureValue(from) ? to : entry,
      );

      try {
        await qaApi.features.update(
          appId,
          feature.id,
          buildFeatureUpdatePayloadFromFeature(feature, { subFeatures: nextSubFeatures }),
        );

        setFeatures((prev) =>
          prev.map((f) =>
            f.id === feature.id
              ? { ...f, subFeatures: nextSubFeatures }
              : f,
          ),
        );

        setFeatureResults((prev) => {
          const existing = prev?.[feature.id] || {};
          const fromKey = Object.keys(existing).find(
            (k) => normalizeSubFeatureValue(k) === normalizeSubFeatureValue(from),
          );
          if (!fromKey) return prev;
          const moved = existing[fromKey];
          const { [fromKey]: _, ...rest } = existing;
          return {
            ...prev,
            [feature.id]: {
              ...rest,
              [to]: moved,
            },
          };
        });

        message.success("Test case updated.");
        return true;
      } catch {
        message.error("Failed to update test case.");
        return false;
      }
    },
    [appId, buildFeatureUpdatePayloadFromFeature],
  );

  const startInlineEdit = useCallback((row) => {
    setEditingCellKey(row?.key || null);
    setEditingCellValue(String(row?.subFeature || ""));
  }, []);

  const cancelInlineEdit = useCallback(() => {
    setEditingCellKey(null);
    setEditingCellValue("");
  }, []);

  const startFeatureNameEdit = useCallback((row) => {
    const featureId = row?.feature?.id || null;
    if (!featureId) return;
    setActiveRowKey(row?.key || null);
    setEditingFeatureId(featureId);
    setEditingFeatureValue(String(row?.feature?.name || ""));
    // Close sub-feature editing if open.
    setEditingCellKey(null);
    setEditingCellValue("");
  }, []);

  const cancelFeatureNameEdit = useCallback(() => {
    setEditingFeatureId(null);
    setEditingFeatureValue("");
  }, []);

  const saveFeatureNameEdit = useCallback(
    async (row, nextValue) => {
      const feature = row?.feature;
      if (!feature?.id) return;
      const trimmed = String(nextValue || "").trim();
      if (!trimmed) {
        message.warning("Feature name cannot be empty.");
        return;
      }

      try {
        await qaApi.features.update(
          appId,
          feature.id,
          buildFeatureUpdatePayloadFromFeature(feature, { name: trimmed }),
        );
        setFeatures((prev) =>
          prev.map((f) => (f.id === feature.id ? { ...f, name: trimmed } : f)),
        );
        message.success("Feature updated.");
        return true;
      } catch {
        message.error("Failed to update feature.");
        return false;
      }
    },
    [appId, buildFeatureUpdatePayloadFromFeature],
  );

  const openCreateFeatureDrawer = () => {
    setEditingFeature(null);
    featureForm.setFieldsValue({
      featureName: "",
      category: "General",
      priority: "MEDIUM",
      status: "ACTIVE",
      description: "",
      subFeatures: DEFAULT_SUB_FEATURES,
    });
    setFeatureAttachments([]);
    setFeatureDrawerOpen(true);
  };

  const openEditFeatureDrawer = (feature) => {
    setEditingFeature(feature);
    featureForm.setFieldsValue({
      featureName: feature.name,
      category: feature.category || "General",
      priority: feature.priority || "MEDIUM",
      status: feature.status || "ACTIVE",
      description: feature.description || "",
      subFeatures: normalizeSubFeatures(extractRawSubFeatures(feature)),
    });
    setFeatureAttachments(
      (feature.attachments || []).map((attachment) => ({
        uid: attachment.fileId || attachment.id,
        fileId: attachment.fileId,
        name: attachment.name,
        size: attachment.size,
        status: "done",
        url: attachment.url || "",
      })),
    );
    setFeatureDrawerOpen(true);
  };

  const closeFeatureDrawer = () => {
    setFeatureDrawerOpen(false);
    setEditingFeature(null);
    setFeatureAttachments([]);
    featureForm.resetFields();
  };

  const saveFeature = async (values) => {
    const payload = buildFeaturePayload({
      ...values,
      attachments: featureAttachments.map((attachment) => ({
        fileId: attachment.fileId,
        name: attachment.name,
        size: attachment.size,
      })),
    });
    if (!payload.name) {
      message.warning("Feature name is required");
      return;
    }

    setFeatureSubmitting(true);
    try {
      if (editingFeature?.id) {
        await qaApi.features.update(appId, editingFeature.id, payload);
        message.success("Feature updated.");
      } else {
        await qaApi.features.create(appId, payload);
        message.success("Feature created.");
      }

      closeFeatureDrawer();
      fetchFeatures();
    } catch (err) {
      message.error("Failed to save feature");
    } finally {
      setFeatureSubmitting(false);
    }
  };

  const deleteFeature = async (feature) => {
    try {
      await qaApi.features.delete(appId, feature.id);
      message.success("Feature deleted.");
      fetchFeatures();
    } catch (err) {
      message.error("Failed to delete feature");
    }
  };

  const extractRunAttachments = (run) =>
    run?.attachments ||
    run?.files ||
    run?.fileAttachments ||
    run?.evidence ||
    run?.documents ||
    run?.run?.attachments ||
    run?.run?.files ||
    run?.run?.fileAttachments ||
    run?.run?.documents ||
    run?.run?.evidence ||
    run?.runAttachments ||
    run?.runFiles ||
    [];

  const normalizeRunAttachmentsForUpload = async (attachments) => {
    const normalized = normalizeAttachments(attachments);
    const uniqueByFileId = new Map();

    normalized.forEach((attachment) => {
      const fileId = getAttachmentFileId(attachment);
      if (!fileId) return;
      if (!uniqueByFileId.has(fileId)) uniqueByFileId.set(fileId, attachment);
    });

    const unique = Array.from(uniqueByFileId.values());
    const uploadItems = await Promise.all(
      unique.map(async (attachment) => {
        const fileId = getAttachmentFileId(attachment);
        const name = getAttachmentName(attachment);
        const size = attachment.size;
        let url = getAttachmentUrl(attachment) || (fileId ? `${STORAGE_BASE_URL}/files/${fileId}` : "");

        if (fileId && isImageAttachment({ ...attachment, name, url })) {
          const signedUrl = await fetchSignedUrlForFileId(fileId);
          if (signedUrl) url = signedUrl;
        }

        return {
          uid: fileId,
          fileId,
          name,
          size,
          status: "done",
          url,
          thumbUrl: url,
        };
      }),
    );

    return uploadItems.filter((item) => Boolean(item?.fileId));
  };

  const openResultDrawer = async (feature, subFeature, options = {}) => {
    const mode = options?.mode || "new"; // "new" | "edit"
    const existing = featureResults[feature.id]?.[subFeature];

    setEditingResultContext({
      featureId: feature.id,
      featureName: feature.name,
      subFeature,
      resultId: existing?.id || null,
    });

    const initial = {
      result: mode === "edit" ? existing?.result || null : null,
      testDate:
        mode === "edit" && existing?.testDate ? dayjs(existing.testDate) : dayjs(),
      notes: mode === "edit" ? existing?.notes || "" : "",
    };

    resultForm.setFieldsValue(initial);
    setRunAttachments([]);
    setResultDrawerOpen(true);

    if (mode !== "edit") return;

    setPrefillAttachmentsLoading(true);
    try {
      const history = await qaApi.features.getResultsHistory(
        appId,
        feature.id,
        subFeature,
        { page: 0, size: 1 },
      );
      const source =
        history?.content ||
        history?.runs ||
        history?.history ||
        history?.items ||
        history?.results ||
        [];
      const latestRun = toArray(source)[0] || null;
      const attachments = normalizeRunAttachmentsForUpload(
        extractRunAttachments(latestRun),
      );

      const parsedNotes = latestRun?.notes ? parseRunNotes(latestRun.notes) : null;
      if (parsedNotes?.notes && !initial.notes) {
        resultForm.setFieldValue("notes", parsedNotes.notes);
      }

      const resolved = await attachments;
      setRunAttachments(resolved);
    } catch (err) {
      message.error("Failed to load existing attachments");
    } finally {
      setPrefillAttachmentsLoading(false);
    }
  };

  const closeResultDrawer = () => {
    setResultDrawerOpen(false);
    setEditingResultContext(null);
    setRunAttachments([]);
    setPrefillAttachmentsLoading(false);
    resultForm.resetFields();
  };

  const uploadAttachment = async (file) => {
    const localPreviewUrl = isImageAttachment({ name: file?.name })
      ? URL.createObjectURL(file)
      : "";

    const uploadResponse = await uploadFile(user?.id, file, {
      purpose: "USER_PROFILE",
      fileType: "DOCUMENT",
    });

    let metadata = null;
    try {
      metadata = await getFileMetadata(uploadResponse.fileId);
    } catch {
      metadata = null;
    }

    let url =
      metadata?.publicUrl ||
      metadata?.url ||
      metadata?.fileUrl ||
      metadata?.downloadUrl ||
      metadata?.signedUrl ||
      metadata?.uri ||
      uploadResponse.file?.publicUrl ||
      uploadResponse.file?.url ||
      uploadResponse.file?.fileUrl ||
      uploadResponse.file?.downloadUrl ||
      uploadResponse.file?.signedUrl ||
      uploadResponse.file?.uri ||
      `${STORAGE_BASE_URL}/files/${uploadResponse.fileId}`;

    if (isImageAttachment({ name: file?.name, url }) && uploadResponse.fileId) {
      const signedUrl = await fetchSignedUrlForFileId(uploadResponse.fileId);
      if (signedUrl) url = signedUrl;
    }

    return {
      uid: uploadResponse.fileId,
      fileId: uploadResponse.fileId,
      name: file.name,
      size: file.size,
      status: "done",
      url: url || localPreviewUrl,
      thumbUrl: localPreviewUrl || url,
    };
  };

  const openAttachmentPreview = async (file) => {
    const fileId = file?.fileId || file?.uid;
    let url =
      file?.url ||
      file?.thumbUrl ||
      file?.response?.url ||
      file?.response?.uri ||
      "";

    if (fileId && isImageAttachment({ name: file?.name, url })) {
      const signedUrl = await fetchSignedUrlForFileId(fileId);
      if (signedUrl) url = signedUrl;
    }

    if (!url && fileId) {
      try {
        const metadata = await getFileMetadata(fileId);
        url =
          metadata?.publicUrl ||
          metadata?.url ||
          metadata?.fileUrl ||
          metadata?.downloadUrl ||
          metadata?.signedUrl ||
          metadata?.uri ||
          `${STORAGE_BASE_URL}/files/${fileId}`;
      } catch {
        url = `${STORAGE_BASE_URL}/files/${fileId}`;
      }
    }

    setAttachmentPreviewTitle(file?.name || "Attachment Preview");
    setAttachmentPreviewUrl(url);
    setAttachmentPreviewIsImage(isImageAttachment({ name: file?.name, url }));
    setAttachmentPreviewOpen(true);
  };

  const validateAttachmentBeforeUpload = (file, attachments, label) => {
    if (attachments.length >= MAX_ATTACHMENTS_PER_RECORD) {
      message.warning(
        `You can attach up to ${MAX_ATTACHMENTS_PER_RECORD} files per ${label}.`,
      );
      return false;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      message.warning(`"${file.name}" exceeds the 10MB limit.`);
      return false;
    }

    return true;
  };

  const handleAttachmentUpload = async ({ file, onSuccess, onError }) => {
    try {
      if (!validateAttachmentBeforeUpload(file, runAttachments, "test run")) {
        onError?.(new Error("Attachment validation failed"));
        return;
      }
      const uploadedAttachment = await uploadAttachment(file);
      setRunAttachments((prev) => [...prev, uploadedAttachment]);
      message.success(`Uploaded ${file.name}`);
      onSuccess?.(uploadedAttachment);
    } catch (error) {
      message.error(error.message || "Failed to upload attachment");
      onError?.(error);
    }
  };

  const handleAttachmentRemove = (file) => {
    setRunAttachments((prev) =>
      prev.filter((attachment) => attachment.uid !== file.uid),
    );
    message.success(`Removed ${file.name}`);
    return true;
  };

  const saveResult = async (values) => {
    if (!editingResultContext?.featureId || !editingResultContext?.subFeature)
      return;

    if (!values.result) {
      message.warning("Please select Pass or Fail");
      return;
    }

    const payload = buildResultPayload(editingResultContext.subFeature, values);
    payload.testedBy =
      user?.fullName ||
      `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
      user?.username ||
      user?.email ||
      user?.id ||
      null;
    const hasNotes = Boolean(payload.notes.trim());
    const hasAttachments = runAttachments.length > 0;
    if (payload.result === "FAIL" && !hasNotes && !hasAttachments) {
      message.warning("FAIL tests need at least a note or one attachment.");
      return;
    }
    const runPayload = {
      result: payload.result,
      testDate: payload.testDate,
      notes: buildRunNotes(editingResultContext.subFeature, payload.notes),
      attachments: runAttachments.map((attachment) => ({
        fileId: attachment.fileId,
        name: attachment.name,
        size: attachment.size,
      })),
    };

    setResultSubmitting(true);
    try {
      if (editingResultContext.resultId) {
        await qaApi.features.updateResult(
          appId,
          editingResultContext.featureId,
          editingResultContext.resultId,
          payload,
        );
      } else {
        await qaApi.features.saveResult(
          appId,
          editingResultContext.featureId,
          payload,
        );
      }
      await qaApi.runs.create(
        appId,
        editingResultContext.featureId,
        runPayload,
      );

      message.success("Result saved.");
      closeResultDrawer();
      fetchFeatures();
    } catch (err) {
      message.error("Failed to save result");
    } finally {
      setResultSubmitting(false);
    }
  };

  const deleteSubFeatureResult = async (feature, subFeature) => {
    const existing = featureResults[feature.id]?.[subFeature];

    if (!existing?.id) {
      message.info(`No saved result found for ${subFeature}.`);
      return;
    }

    try {
      await qaApi.features.deleteResult(appId, feature.id, existing.id);
      message.success("Result deleted.");
      fetchFeatures();
    } catch (err) {
      message.error("Failed to delete result");
    }
  };

  const openTestCasesDrawer = (feature) => {
    setTestCasesDrawerFeature(feature);
    setTestCasesSearch("");
    setTestCasesResultFilter("ALL");
    setTestCasesPagination({ current: 1, pageSize: 10 });
    setTestCasesDrawerOpen(true);
  };

  const openAddTestCaseForFeature = (feature) => {
    if (!feature?.id) return;
    // Don't switch the user into the "view all test cases" drawer when they just want to add one.
    setTestCasesDrawerOpen(false);
    setTestCasesDrawerFeature(feature);
    addTestCaseForm.setFieldsValue({ testCase: "" });
    setAddTestCaseDrawerOpen(true);
  };

  const closeTestCasesDrawer = () => {
    setTestCasesDrawerOpen(false);
    setTestCasesDrawerFeature(null);
    setTestCasesSearch("");
    setTestCasesResultFilter("ALL");
    setTestCasesPagination({ current: 1, pageSize: 10 });
  };

  const openDetailDrawer = (feature, subFeature = null) => {
    setDetailContext({ feature, subFeature });
    setDetailDrawerOpen(true);
  };

  const closeDetailDrawer = () => {
    setDetailDrawerOpen(false);
    setDetailContext(null);
  };

  const [addTestCaseDrawerOpen, setAddTestCaseDrawerOpen] = useState(false);
  const [addTestCaseSubmitting, setAddTestCaseSubmitting] = useState(false);
  const [editTestCaseDrawerOpen, setEditTestCaseDrawerOpen] = useState(false);
  const [editTestCaseSubmitting, setEditTestCaseSubmitting] = useState(false);
  const [editTestCaseContext, setEditTestCaseContext] = useState(null);

  const openAddTestCaseDrawer = () => {
    addTestCaseForm.setFieldsValue({ testCase: "" });
    setAddTestCaseDrawerOpen(true);
  };

  const closeAddTestCaseDrawer = () => {
    setAddTestCaseDrawerOpen(false);
    addTestCaseForm.resetFields();
  };

  const [editTestCaseForm] = Form.useForm();

  const openEditTestCaseDrawer = (featureRecord, subFeature) => {
    if (!featureRecord?.id || !subFeature) return;
    setEditTestCaseContext({
      featureId: featureRecord.id,
      featureName: featureRecord.name,
      subFeature,
    });
    editTestCaseForm.setFieldsValue({ testCase: String(subFeature || "").trim() });
    setEditTestCaseDrawerOpen(true);
  };

  const closeEditTestCaseDrawer = () => {
    if (editTestCaseSubmitting) return;
    setEditTestCaseDrawerOpen(false);
    setEditTestCaseContext(null);
    editTestCaseForm.resetFields();
  };

  const renameTestCase = async (values) => {
    const context = editTestCaseContext;
    if (!context?.featureId) return;
    const oldName = String(context.subFeature || "").trim();
    const newName = String(values?.testCase || "").trim();

    if (!newName) {
      message.warning("Test case name is required.");
      return;
    }

    if (normalizeSubFeatureValue(newName) === normalizeSubFeatureValue(oldName)) {
      message.info("No changes to save.");
      closeEditTestCaseDrawer();
      return;
    }

    const loadingKey = `rename-test-case-${context.featureId}-${oldName}`;
    setEditTestCaseSubmitting(true);
    try {
      message.loading({
        content: `Renaming "${oldName}"...`,
        key: loadingKey,
        duration: 0,
      });

      const latestFeature = await qaApi.features.get(appId, context.featureId);
      const latestSubFeatures = normalizeSubFeatures(extractRawSubFeatures(latestFeature));

      const existingIndex = latestSubFeatures.findIndex(
        (entry) => normalizeSubFeatureValue(entry) === normalizeSubFeatureValue(oldName),
      );
      if (existingIndex === -1) {
        message.error({ content: "Test case not found.", key: loadingKey });
        return;
      }

      const alreadyExists = latestSubFeatures.some(
        (entry, idx) =>
          idx !== existingIndex &&
          normalizeSubFeatureValue(entry) === normalizeSubFeatureValue(newName),
      );
      if (alreadyExists) {
        message.info({ content: `"${newName}" already exists.`, key: loadingKey });
        return;
      }

      const nextSubFeatures = [...latestSubFeatures];
      nextSubFeatures[existingIndex] = newName;

      // Best-effort: rename the saved "result" record to match the new test case.
      const latestResultsResponse = await qaApi.features
        .getResults(appId, context.featureId)
        .catch(() => null);
      const latestResults = latestResultsResponse?.results || {};
      const matchKey = Object.keys(latestResults).find(
        (key) => normalizeSubFeatureValue(key) === normalizeSubFeatureValue(oldName),
      );
      const existingResult = matchKey ? latestResults[matchKey] : null;

      if (existingResult?.id) {
        await qaApi.features
          .updateResult(appId, context.featureId, existingResult.id, {
            subFeature: newName,
            result: existingResult.result,
            testDate: existingResult.testDate,
            notes: existingResult.notes || "",
          })
          .catch(() => null);
      }

      await qaApi.features.update(
        appId,
        context.featureId,
        buildFeatureUpdatePayloadFromFeature(latestFeature, {
          subFeatures: nextSubFeatures,
        }),
      );

      // If details drawer is open for this test case, keep it open and point to new name.
      if (
        detailContext?.feature?.id &&
        String(detailContext.feature.id) === String(context.featureId) &&
        normalizeSubFeatureValue(detailContext.subFeature) ===
          normalizeSubFeatureValue(oldName)
      ) {
        setDetailContext((prev) =>
          prev ? { ...prev, subFeature: newName } : prev,
        );
      }

      message.success({ content: "Test case renamed.", key: loadingKey });
      closeEditTestCaseDrawer();
      await fetchFeatures();
    } catch (err) {
      console.error("Failed to rename test case", err);
      message.error({ content: "Failed to rename test case", key: loadingKey });
    } finally {
      setEditTestCaseSubmitting(false);
    }
  };

  const saveNewTestCase = async (values) => {
    const feature = testCasesDrawerFeature;
    if (!feature?.id) return;

    const testCase = String(values?.testCase || "").trim();
    if (!testCase) {
      message.warning("Test case name is required.");
      return;
    }

    const existing = normalizeSubFeatures(extractRawSubFeatures(feature));

    const exists = existing.some(
      (entry) => entry.toLowerCase() === testCase.toLowerCase(),
    );
    if (exists) {
      message.info(`"${testCase}" already exists.`);
      return;
    }

    const nextSubFeatures = [...existing, testCase].filter(Boolean);

    const payload = {
      name: feature.name,
      description: feature.description || "",
      category: feature.category || "General",
      priority: feature.priority || "MEDIUM",
      status: feature.status || "ACTIVE",
      subFeatures: nextSubFeatures,
      attachments: normalizeAttachments(feature.attachments || []).map(
        (attachment) => ({
          fileId: getAttachmentFileId(attachment),
          name: getAttachmentName(attachment),
          size: attachment.size,
        }),
      ),
    };

    setAddTestCaseSubmitting(true);
    try {
      await qaApi.features.update(appId, feature.id, payload);
      message.success("Test case added.");
      await fetchFeatures();

      closeAddTestCaseDrawer();
    } catch (err) {
      message.error("Failed to add test case");
    } finally {
      setAddTestCaseSubmitting(false);
    }
  };

  const testCasesDrawerRows = useMemo(() => {
    if (!testCasesDrawerFeature?.id) return [];
    const feature = testCasesDrawerFeature;
    const results = featureResults[feature.id] || {};
    const subFeatures = normalizeSubFeatures(extractRawSubFeatures(feature));

    return subFeatures.map((subFeature) => {
      const data = results[subFeature] || null;
      const status = data?.result || "NONE";

      return {
        key: `${feature.id}-${subFeature}`,
        subFeature,
        status,
        testDate: data?.testDate || null,
        notes: data?.notes || "",
      };
    });
  }, [featureResults, testCasesDrawerFeature]);

  useEffect(() => {
    if (!features.length) return;

    if (testCasesDrawerFeature?.id) {
      const updated = features.find(
        (entry) => String(entry.id) === String(testCasesDrawerFeature.id),
      );
      if (updated && updated !== testCasesDrawerFeature) {
        setTestCasesDrawerFeature(updated);
      }
    }

    if (detailContext?.feature?.id) {
      const updated = features.find(
        (entry) => String(entry.id) === String(detailContext.feature.id),
      );
      if (updated && updated !== detailContext.feature) {
        setDetailContext((prev) =>
          prev ? { ...prev, feature: updated } : prev,
        );
      }
    }
  }, [features, testCasesDrawerFeature, detailContext]);

  const removeTestCase = async (featureRecord, subFeature) => {
    Modal.confirm({
      title: "Delete Test Case",
      content: `Delete "${subFeature}" from "${featureRecord?.name}" (this will also delete its saved result and run history)?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      zIndex: MODAL_Z_INDEX,
      getContainer: () => document.body,
      onOk: async () => {
        const loadingKey = `delete-test-case-${featureRecord?.id}-${subFeature}`;
        try {
          message.loading({
            content: `Deleting "${subFeature}"...`,
            key: loadingKey,
            duration: 0,
          });

          const latestFeature = await qaApi.features.get(appId, featureRecord.id);
          const latestSubFeatures = normalizeSubFeatures(
            extractRawSubFeatures(latestFeature),
          );
          const nextSubFeatures = latestSubFeatures.filter(
            (entry) =>
              normalizeSubFeatureValue(entry) !== normalizeSubFeatureValue(subFeature),
          );

          if (nextSubFeatures.length === 0) {
            message.warning({
              content: "A feature must have at least one test case.",
              key: loadingKey,
            });
            return;
          }

          // Delete the saved result record (best-effort).
          const latestResultsResponse = await qaApi.features
            .getResults(appId, featureRecord.id)
            .catch(() => null);
          const latestResults = latestResultsResponse?.results || {};
          const existingResult = latestResults?.[subFeature] || null;
          if (existingResult?.id) {
            await qaApi.features
              .deleteResult(appId, featureRecord.id, existingResult.id)
              .catch(() => null);
          }

          // Delete all runs associated with this test case (best-effort). Prefer the
          // feature history endpoint because it is already used elsewhere in this page.
          try {
            const normalizedTarget = normalizeSubFeatureValue(subFeature);
            const pageSize = 500;
            let currentPage = 0;
            let hasMore = true;
            const runIdsToDelete = [];

            while (hasMore && currentPage < 20) {
              const historyResponse = await qaApi.features.getHistory(
                appId,
                featureRecord.id,
                { page: currentPage, size: pageSize },
              );

              const historyItemsSource =
                historyResponse?.content ||
                historyResponse?.runs ||
                historyResponse?.history ||
                historyResponse?.items ||
                historyResponse?.results ||
                [];

              const historyItems = toArray(historyItemsSource);

              historyItems.forEach((entry) => {
                const parsed = parseRunNotes(entry?.notes);
                const entrySubFeature = normalizeSubFeatureValue(
                  entry?.subFeature || parsed.subFeature,
                );
                if (entrySubFeature && entrySubFeature === normalizedTarget) {
                  const runId = entry?.id || entry?.runId;
                  if (runId) runIdsToDelete.push(runId);
                }
              });

              const totalFromApi =
                historyResponse?.totalElements ??
                historyResponse?.total ??
                historyResponse?.page?.totalElements ??
                historyResponse?.pagination?.totalElements;

              if (typeof totalFromApi === "number") {
                hasMore = (currentPage + 1) * pageSize < totalFromApi;
              } else {
                hasMore = historyItems.length === pageSize;
              }

              currentPage += 1;
            }

            const uniqueRunIds = [...new Set(runIdsToDelete)];
            const concurrency = 5;
            for (
              let index = 0;
              index < uniqueRunIds.length;
              index += concurrency
            ) {
              const batch = uniqueRunIds.slice(index, index + concurrency);
              await Promise.all(
                batch.map((runId) =>
                  qaApi.runs
                    .delete(appId, featureRecord.id, runId)
                    .catch(() => null),
                ),
              );
            }
          } catch (historyDeleteError) {
            console.error("Failed to delete run history for test case", {
              historyDeleteError,
              featureId: featureRecord?.id,
              subFeature,
            });
          }

          // Finally remove the test case from the feature itself.
          await qaApi.features.update(
            appId,
            featureRecord.id,
            buildFeatureUpdatePayloadFromFeature(latestFeature, {
              subFeatures: nextSubFeatures,
            }),
          );

          if (
            detailContext?.feature?.id &&
            String(detailContext.feature.id) === String(featureRecord.id) &&
            normalizeSubFeatureValue(detailContext.subFeature) ===
              normalizeSubFeatureValue(subFeature)
          ) {
            closeDetailDrawer();
          }

          message.success({ content: "Test case deleted.", key: loadingKey });
          await fetchFeatures();
        } catch (err) {
          console.error("Failed to delete test case", err);
          message.error({ content: "Failed to delete test case", key: loadingKey });
        }
      },
    });
  };

  const filteredTestCasesDrawerRows = useMemo(() => {
    const term = testCasesSearch.trim().toLowerCase();
    const normalizedResult = String(testCasesResultFilter || "ALL").toUpperCase();

    return testCasesDrawerRows.filter((row) => {
      if (normalizedResult !== "ALL" && row.status !== normalizedResult)
        return false;
      if (!term) return true;
      return String(row.subFeature || "").toLowerCase().includes(term);
    });
  }, [testCasesDrawerRows, testCasesSearch, testCasesResultFilter]);

  const openHistoryPage = useCallback(
    (featureRecord, subFeature = null) => {
      if (!featureRecord?.id) return;
      const base = paths.qaTestRunsFeatureHistory(appId, featureRecord.id);
      const query = subFeature
        ? `?subFeature=${encodeURIComponent(String(subFeature))}`
        : "";

      navigate(`${base}${query}`, {
        state: {
          app,
          feature: featureRecord,
          from: `${location.pathname}${location.search}`,
          fromState: {
            ...(location.state || {}),
            restore: {
              searchText,
              statusFilter,
              resultFilter,
              sortMode,
              page,
              pageSize,
              casePage,
              casePageSize,
            },
          },
        },
      });
    },
    [appId, navigate, app, location.pathname, location.search, location.state, searchText, statusFilter, resultFilter, sortMode, page, pageSize, casePage, casePageSize],
  );

  const getFeatureActions = (record) => ({
    items: [

      {
        key: "addTestCase",
        label: "Add Test Case",
        icon: <PlusOutlined />,
        onClick: () => openAddTestCaseForFeature(record),
      },

      {
        key: "edit",
        label: "Edit Feature Details",
        icon: <EditOutlined />,
        onClick: () => openEditFeatureDrawer(record),
      },
      {
        key: "history",
        label: "View History",
        icon: <HistoryOutlined />,
        onClick: () => openHistoryPage(record),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <DeleteOutlined />,
        onClick: () => {
          Modal.confirm({
            title: "Delete Feature",
            content: `Are you sure you want to delete "${record.name}"?`,
            okText: "Delete",
            okButtonProps: { danger: true },
            zIndex: MODAL_Z_INDEX,
            getContainer: () => document.body,
            onOk: () => deleteFeature(record),
          });
        },
      },
    ],
  });

  const getSubFeatureActions = (featureRecord, subFeature) => ({
    items: [
      {
        key: "recordTest",
        label: "Record New Test",
        icon: <EditOutlined />,
        onClick: () => openResultDrawer(featureRecord, subFeature),
      },
      {
        key: "editCase",
        label: "Edit Case",
        icon: <EditOutlined />,
        onClick: () => openResultDrawer(featureRecord, subFeature, { mode: "edit" }),
      },
      {
        key: "renameTestCase",
        label: "Rename Test Case",
        icon: <EditOutlined />,
        onClick: () => openEditTestCaseDrawer(featureRecord, subFeature),
      },
      {
        key: "removeTestCase",
        label: "Delete Test Case",
        danger: true,
        icon: <DeleteOutlined />,
        onClick: () => removeTestCase(featureRecord, subFeature),
      },
      {
        key: "history",
        label: "View History",
        icon: <HistoryOutlined />,
        onClick: () => openHistoryPage(featureRecord, subFeature),
      },
    ],
  });

  if (appError || !app) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Failed to load this test app"
          description="Please check the URL or go back to Testable Apps."
        />
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <Button onClick={() => navigate(paths.qaTestRuns)}>
            Back to Testable Apps
          </Button>
          <Button onClick={() => fetchApp({ silent: false })}>Retry</Button>
        </div>
      </Card>
    );
  }

	  const columns = [
	    {
	      title: "Feature",
	      dataIndex: "name",
	      key: "feature",
	      render: (value, record) => (
	        <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Button
                type="link"
                style={{
                  paddingInline: 0,
                  paddingBlock: 0,
                  height: "auto",
                  flex: 1,
                  minWidth: 0,
                  textAlign: "left",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  openDetailDrawer(record);
                }}
              >
                <Text strong style={{ whiteSpace: "normal" }}>
                  {value}
                </Text>
              </Button>
              <Dropdown trigger={["click"]} menu={getFeatureActions(record)}>
                <Button
                  type="text"
                  size="small"
                  icon={<EllipsisOutlined />}
                  aria-label="Feature actions"
                  onClick={(event) => event.stopPropagation()}
                />
              </Dropdown>
            </div>
	        </Space>
	      ),
	    },
    {
      title: "Status",
      dataIndex: "status",
      key: "featureStatus",
      render: (value) => (
        <Tag color={getFeatureStatusColor(value)}>
          {String(value || "ACTIVE").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (value) => <Tag>{value || "MEDIUM"}</Tag>,
    },
    {
      title: "Passes",
      key: "passes",
      align: "right",
      render: (_, record) => {
        const counts = featureCountsById[record.id] || {
          passes: 0,
          fails: 0,
          untested: 0,
        };
        return <Text style={{ color: "#16a34a" }}>{counts.passes}</Text>;
      },
    },
    {
      title: "Fails",
      key: "fails",
      align: "right",
      render: (_, record) => {
        const counts = featureCountsById[record.id] || {
          passes: 0,
          fails: 0,
          untested: 0,
        };
        return <Text style={{ color: "#dc2626" }}>{counts.fails}</Text>;
      },
    },
    {
      title: "Untested",
      key: "untested",
      align: "right",
      render: (_, record) => {
        const counts = featureCountsById[record.id] || {
          passes: 0,
          fails: 0,
          untested: 0,
        };
        return <Text type="secondary">{counts.untested}</Text>;
      },
    },
  ];

  const testCasesDrawerColumns = [
    {
      title: "Test Case",
      dataIndex: "subFeature",
      key: "subFeature",
      render: (value) =>
        testCasesDrawerFeature ? (
          <Button
            type="link"
            style={{ paddingInline: 0, paddingBlock: 0, height: "auto" }}
            onClick={() => openDetailDrawer(testCasesDrawerFeature, value)}
          >
            <Text strong>{value}</Text>
          </Button>
        ) : (
          <Text strong>{value}</Text>
        ),
    },
    {
      title: "Test Date",
      key: "testDate",
      render: (_, row) => (row.testDate ? formatDateTime(row.testDate) : "-"),
    },
    {
      title: "Notes",
      key: "notes",
      render: (_, row) => {
        if (!row.notes) return "-";
        return (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.notes}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) => (
        <Space>
          {statusIcons[row.status] || statusIcons.NONE}
          {statusTags[row.status] || statusTags.NONE}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, row) =>
        testCasesDrawerFeature ? (
          <Dropdown
            trigger={["click"]}
            menu={getSubFeatureActions(testCasesDrawerFeature, row.subFeature)}
          >
            <Button type="text" size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        ) : null,
    },
  ];

  const handleTestCasesDrawerTableChange = (pag) => {
    setTestCasesPagination({
      current: pag.current || 1,
      pageSize: pag.pageSize || 10,
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 mt-3 gap-3">
        <WidgetSectionTitle
          icon="bug"
          title="Testable App Features"
          subtitle={'View list of app features and carry out current test runs on each of them'}
          className="flex-1"
        />
        <div className="d-flex justify-content-end mb-2">
          <IconButton
            variant="primary"
            size="sm"
            className="text-sm me-2"
            onClick={() => navigate(paths.qaTestRuns)}
          >
            Go Back
          </IconButton>
        </div>
      </div>

      <Spin spinning={appLoading} tip={null}>
        <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
          <Space wrap>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search features..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              allowClear
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder="Filter"
              value={(() => {
                const normalizedStatus = String(statusFilter || "ALL").toUpperCase();
                if (normalizedStatus !== "ALL") return normalizedStatus;
                const normalizedSort = String(sortMode || "STABLE").toUpperCase();
                if (normalizedSort === "RECENT") return "LAST_TESTED";
                return undefined;
              })()}
              style={{ width: 170 }}
              options={COMBINED_FILTER_OPTIONS}
              onChange={(value) => {
                const raw = String(value || "");
                if (!raw) {
                  setStatusFilter("ALL");
                  setSortMode("STABLE");
                  setPage(0);
                  return;
                }
                if (raw === "LAST_TESTED") {
                  setStatusFilter("ALL");
                  setSortMode("RECENT");
                  setPage(0);
                  return;
                }
                if (raw === "ACTIVE" || raw === "INACTIVE" || raw === "ARCHIVED") {
                  setStatusFilter(raw);
                  setSortMode("STABLE");
                  setPage(0);
                  return;
                }
                setStatusFilter("ALL");
                setSortMode("STABLE");
                setPage(0);
              }}
            />
            <Select
              value={resultFilter}
              style={{ width: 170 }}
              options={[
                { label: "All Results", value: "ALL" },
                { label: "Passes", value: "PASS" },
                { label: "Fails", value: "FAIL" },
                { label: "Untested", value: "NONE" },
              ]}
              onChange={(value) => setResultFilter(value || "ALL")}
            />
          </Space>
          <IconButton
            variant="success"
            icon="plus"
            size="sm"
            className="text-xs text-white px-3 fw-normal"
            onClick={openCreateFeatureDrawer}
            style={{ height: 32 }}
          >
            Add Feature
          </IconButton>
        </div>

        <Table
          columns={[
            {
              title: "Feature",
              key: "featureName",
              ellipsis: true,
              render: (_, row) => {
                const rowSpan = featureRowSpanByKey.get(row.key) ?? 1;
                const featureId = row?.feature?.id || null;
                const isEditing = rowSpan > 0 && featureId && String(editingFeatureId) === String(featureId);
                return {
                  children: (
                    isEditing ? (
                      <Space size={8} style={{ width: "100%" }}>
                        <Input
                          size="small"
                          autoFocus
                          value={editingFeatureValue}
                          onChange={(event) => setEditingFeatureValue(event.target.value)}
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") {
                              event.preventDefault();
                              cancelFeatureNameEdit();
                            }
                            if (event.key === "Enter") {
                              event.preventDefault();
                            }
                          }}
                        />
                        <CheckOutlined
                          onClick={async (event) => {
                            event.stopPropagation();
                            const featureId = row?.feature?.id || null;
                            if (!featureId) return;
                            setSavingFeatureId(featureId);
                            const ok = await saveFeatureNameEdit(row, editingFeatureValue);
                            setSavingFeatureId(null);
                            if (ok) cancelFeatureNameEdit();
                          }}
                          style={{
                            cursor:
                              savingFeatureId === row?.feature?.id
                                ? "not-allowed"
                                : "pointer",
                            color: "#16a34a",
                            opacity:
                              savingFeatureId === row?.feature?.id ? 0.6 : 1,
                          }}
                        />
                        <CloseOutlined
                          onClick={(event) => {
                            event.stopPropagation();
                            cancelFeatureNameEdit();
                          }}
                          style={{ cursor: "pointer", color: "#ef4444" }}
                        />
                      </Space>
                    ) : (
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          if (rowSpan > 0) startFeatureNameEdit(row);
                        }}
                        className={activeRowKey === row.key ? "qa-row-active" : ""}
                        style={{ cursor: rowSpan > 0 ? "text" : "default" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Text strong style={{ whiteSpace: "normal", flex: 1, minWidth: 0 }}>
                            {row.feature?.name || "-"}
                          </Text>
                          {rowSpan > 0 ? (
                            <span
                              onClick={(event) => event.stopPropagation()}
                              onPointerDown={(event) => event.stopPropagation()}
                            >
                              <Dropdown trigger={["click"]} menu={getFeatureActions(row.feature)}>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EllipsisOutlined />}
                                  aria-label="Feature actions"
                                  onClick={(event) => event.stopPropagation()}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onPointerDown={(event) => event.stopPropagation()}
                                />
                              </Dropdown>
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )
                  ),
                  props: {
                    rowSpan,
                    style: rowSpan > 0 ? { verticalAlign: "top", paddingTop: 8 } : undefined,
                  },
                };
              },
            },
            {
              title: "Test Case",
              dataIndex: "subFeature",
              key: "subFeature",
              render: (value, row) => {
                if (editingCellKey === row.key) {
                  return (
                    <Space size={8} style={{ width: "100%" }}>
                      <Input
                        size="small"
                        autoFocus
                        value={editingCellValue}
                        onChange={(event) => setEditingCellValue(event.target.value)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            cancelInlineEdit();
                          }
                          if (event.key === "Enter") {
                            event.preventDefault();
                          }
                        }}
                      />
                      <CheckOutlined
                        onClick={async (event) => {
                          event.stopPropagation();
                          setSavingCellKey(row.key);
                          const ok = await renameSubFeature(
                            row.feature,
                            row.subFeature,
                            editingCellValue,
                          );
                          setSavingCellKey(null);
                          if (ok) cancelInlineEdit();
                        }}
                        style={{
                          cursor:
                            savingCellKey === row.key ? "not-allowed" : "pointer",
                          color: "#16a34a",
                          opacity: savingCellKey === row.key ? 0.6 : 1,
                        }}
                      />
                      <CloseOutlined
                        onClick={(event) => {
                          event.stopPropagation();
                          cancelInlineEdit();
                        }}
                        style={{ cursor: "pointer", color: "#ef4444" }}
                      />
                    </Space>
                  );
                }

                return (
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveRowKey(row.key);
                      startInlineEdit(row);
                      cancelFeatureNameEdit();
                    }}
                    className={activeRowKey === row.key ? "qa-row-active" : ""}
                  >
                    <Text strong style={{ whiteSpace: "normal" }}>
                      {value}
                    </Text>
                  </div>
                );
              },
            },
            {
              title: "Result",
              key: "result",
              render: (_, row) => (
                <Space>
                  {statusIcons[row.result] || statusIcons.NONE}
                  {statusTags[row.result] || statusTags.NONE}
                </Space>
              ),
            },
            {
              title: "Last Run",
              key: "testDate",
              render: (_, row) =>
                row.testDate ? formatDateTime(row.testDate) : "-",
            },
            {
              title: "Tested By",
              key: "testedBy",
              ellipsis: true,
              render: (_, row) => (
                <Text type="secondary" ellipsis={{ tooltip: resolveTesterName(row.testedBy) }}>
                  {resolveTesterName(row.testedBy)}
                </Text>
              ),
            },
            {
              title: "Actions",
              key: "actions",
              align: "center",
              render: (_, row) => (
                <Dropdown
                  trigger={["click"]}
                  menu={getSubFeatureActions(row.feature, row.subFeature)}
                >
                  <Button type="text" size="small" icon={<EllipsisOutlined />} />
                </Dropdown>
              ),
            },
          ]}
          dataSource={pagedTestCaseRows}
          loading={loading}
          pagination={{
            current: casePage + 1,
            pageSize: casePageSize,
            total: (testCaseRows || []).length,
            size: "small",
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (count, range) =>
              `${range[0]}-${range[1]} of ${count} test cases`,
            onChange: (nextPage, nextPageSize) => {
              setCasePage(nextPage - 1);
              setCasePageSize(nextPageSize);
            },
          }}
          rowKey="key"
          bordered
          size="small"
          tableLayout="auto"
          scroll={{ x: "max-content" }}
          className="qa-spreadsheet-table"
          style={{ borderRadius: 8, overflow: "hidden" }}
          locale={{
            emptyText: <Text type="secondary">No test cases found</Text>,
          }}
        />
      </Spin>

      <Drawer
        title={
          editingFeature
            ? `Edit Feature: ${editingFeature.name}`
            : "Create New Feature"
        }
        open={featureDrawerOpen}
        onClose={closeFeatureDrawer}
        width={460}
        zIndex={99999}
        destroyOnClose
      >
        <Form layout="vertical" form={featureForm} onFinish={saveFeature}>
          <Form.Item
            name="featureName"
            label="Feature Name"
            rules={[{ required: true, message: "Feature name is required" }]}
          >
            <Input placeholder="e.g., User Management" maxLength={120} />
          </Form.Item>

          <Form.Item name="category" label="Category">
            <Input placeholder="e.g., Authentication" maxLength={80} />
          </Form.Item>

          <Space style={{ width: "100%" }} size={10}>
            <Form.Item name="priority" label="Priority" style={{ flex: 1 }}>
              <Select options={PRIORITY_OPTIONS} />
            </Form.Item>
            <Form.Item name="status" label="Status" style={{ flex: 1 }}>
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </Space>

          <Form.Item name="description" label="Description">
            <TextArea rows={4} maxLength={500} showCount />
          </Form.Item>

          <Form.Item
            name="subFeatures"
            label="Test Cases"
            rules={[
              {
                validator: async (_, value) => {
                  const items = Array.isArray(value)
                    ? value.map((entry) => String(entry || "").trim()).filter(Boolean)
                    : [];
                  if (items.length === 0) {
                    throw new Error("Add at least one test case.");
                  }
                },
              },
            ]}
          >
            <Select
              mode="tags"
              tokenSeparators={[","]}
              placeholder="Type a test case and press Enter"
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: add/remove test cases like tags (comma also works).
          </Text>

          <Divider />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeFeatureDrawer}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={featureSubmitting}
            >
              {editingFeature ? "Save Changes" : "Create Feature"}
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={
          editingResultContext
            ? `${editingResultContext.featureName} - ${editingResultContext.subFeature}`
            : "Test Result"
        }
        open={resultDrawerOpen}
        onClose={closeResultDrawer}
        width={420}
        zIndex={99999}
        destroyOnClose
      >
        <Form form={resultForm} layout="vertical" onFinish={saveResult}>
          <Form.Item label="Result" required>
            <Space.Compact block>
              <Button
                type={watchedResult === "PASS" ? "primary" : "default"}
                onClick={() => resultForm.setFieldValue("result", "PASS")}
                style={
                  watchedResult === "PASS"
                    ? { backgroundColor: "#16a34a", borderColor: "#16a34a" }
                    : undefined
                }
              >
                Pass
              </Button>
              <Button
                type={watchedResult === "FAIL" ? "primary" : "default"}
                danger={watchedResult === "FAIL"}
                onClick={() => resultForm.setFieldValue("result", "FAIL")}
              >
                Fail
              </Button>
            </Space.Compact>
            <Form.Item
              name="result"
              hidden
              rules={[{ required: true, message: "Select result" }]}
            >
              <Input />
            </Form.Item>
          </Form.Item>

          <Form.Item name="testDate" label="Test Date & Time (auto)">
            <DatePicker
              showTime={{ format: "hh:mm A" }}
              format="MMMM D, YYYY hh:mm A"
              style={{ width: "100%" }}
              placeholder="Select date and time"
              disabled
            />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea
              rows={4}
              placeholder="Add notes about the test..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item label="Attachments">
            <Upload
              multiple
              customRequest={handleAttachmentUpload}
              fileList={runAttachments}
              onRemove={handleAttachmentRemove}
              listType="picture"
              onPreview={openAttachmentPreview}
              disabled={prefillAttachmentsLoading}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {prefillAttachmentsLoading ? "Loading..." : "Upload Attachment"}
              </Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Files uploaded here are attached to the test run history. Max{" "}
              {MAX_ATTACHMENTS_PER_RECORD} files, 10MB each.
            </Text>
          </Form.Item>

          <Divider />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeResultDrawer}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={resultSubmitting}>
              Save Result
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={
          detailContext?.feature
            ? detailContext.subFeature
              ? `Test Case: ${detailContext.feature.name} → ${detailContext.subFeature}`
              : `Feature: ${detailContext.feature.name}`
            : "Details"
        }
        open={detailDrawerOpen}
        onClose={closeDetailDrawer}
        width={920}
        zIndex={100060}
        destroyOnClose
      >
        {detailContext?.feature ? (
          (() => {
            const feature = detailContext.feature;
            const subFeature = detailContext.subFeature;
            const results = featureResults[feature.id] || {};
            const counts = featureCountsById[feature.id] || {
              passes: 0,
              fails: 0,
              untested: 0,
            };

            const latestTestDate = Object.values(results || {}).reduce(
              (latest, entry) => {
                const value = entry?.testDate;
                if (!value) return latest;
                if (!latest) return value;
                return dayjs(value).isAfter(dayjs(latest)) ? value : latest;
              },
              null,
            );

            if (subFeature) {
              const current = results[subFeature] || null;
              const currentStatus = String(current?.result || "NONE").toUpperCase();

              return (
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <Card
                    size="small"
                    style={{ borderRadius: 10 }}
                    styles={{ body: { padding: 12 } }}
                  >
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
                        <Space wrap>
                          {statusIcons[currentStatus] || statusIcons.NONE}
                          {statusTags[currentStatus] || statusTags.NONE}
                        </Space>
                        <Space wrap>
                          <Button
                            type="primary"
                            onClick={() => {
                              closeDetailDrawer();
                              openResultDrawer(feature, subFeature);
                            }}
                          >
                            Record New Test
                          </Button>
                          <Button
                            onClick={() => {
                              closeDetailDrawer();
                              openResultDrawer(feature, subFeature, { mode: "edit" });
                            }}
                          >
                            Edit Case
                          </Button>
                          <Button
                            onClick={() => {
                              closeDetailDrawer();
                              openHistoryPage(feature, subFeature);
                            }}
                          >
                            View History
                          </Button>
                        </Space>
                      </Space>

                      <Divider style={{ marginBlock: 8 }} />

                      <Space wrap size={14}>
                        <Text type="secondary">
                          Test Date & Time: {formatDateTime(current?.testDate)}
                        </Text>
                        <Text type="secondary">
                          Feature Status:{" "}
                          <Tag color={getFeatureStatusColor(feature.status)}>
                            {String(feature.status || "ACTIVE").toUpperCase()}
                          </Tag>
                        </Text>
                      </Space>

                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Notes
                        </Text>
                        <div>
                          <Text>{current?.notes || "-"}</Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Space>
              );
            }

            const detailTestCaseRows = (feature.subFeatures || DEFAULT_SUB_FEATURES).map(
              (name) => {
                const row = results[name] || {};
                return {
                  key: `${feature.id}-${name}`,
                  subFeature: name,
                  status: String(row.result || "NONE").toUpperCase(),
                  testDate: row.testDate || null,
                  notes: row.notes || "",
                };
              },
            );

            return (
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <Card
                  size="small"
                  style={{ borderRadius: 10 }}
                  styles={{ body: { padding: 12 } }}
                >
                  <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
                      <Space wrap>
                        <Tag color={getFeatureStatusColor(feature.status)}>
                          {String(feature.status || "ACTIVE").toUpperCase()}
                        </Tag>
                        <Tag>{String(feature.priority || "MEDIUM").toUpperCase()}</Tag>
                        <Text type="secondary">
                          Last Tested: {formatDateTime(latestTestDate)}
                        </Text>
                      </Space>
                      <Space wrap>
                        <Button
                          onClick={() => {
                            closeDetailDrawer();
                            openEditFeatureDrawer(feature);
                          }}
                        >
                          Edit Feature
                        </Button>
                        <Button
                          onClick={() => {
                            closeDetailDrawer();
                            openHistoryPage(feature);
                          }}
                        >
                          View History
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => {
                            closeDetailDrawer();
                            openTestCasesDrawer(feature);
                          }}
                        >
                          View All Test Cases
                        </Button>
                      </Space>
                    </Space>

                    <Text type="secondary">{feature.description || "-"}</Text>

                    <Space wrap size={12}>
                      <Text style={{ color: "#16a34a" }}>Passes: {counts.passes}</Text>
                      <Text style={{ color: "#dc2626" }}>Fails: {counts.fails}</Text>
                      <Text type="secondary">Untested: {counts.untested}</Text>
                      <Text type="secondary">
                        Files: {feature.attachments?.length || 0}
                      </Text>
                    </Space>
                  </Space>
                </Card>

                <Card
                  size="small"
                  title="Test Cases"
                  style={{ borderRadius: 10 }}
                  styles={{ body: { padding: 0 } }}
                >
                  <Table
                    columns={[
                      {
                        title: "Test Case",
                        dataIndex: "subFeature",
                        key: "subFeature",
                        width: 240,
                        render: (value) => (
                          <Button
                            type="link"
                            style={{
                              paddingInline: 0,
                              paddingBlock: 0,
                              height: "auto",
                            }}
                            onClick={() => openDetailDrawer(feature, value)}
                          >
                            <Text strong>{value}</Text>
                          </Button>
                        ),
                      },
                      {
                        title: "Result",
                        dataIndex: "status",
                        key: "status",
                        width: 160,
                        render: (value) => (
                          <Space>
                            {statusIcons[value] || statusIcons.NONE}
                            {statusTags[value] || statusTags.NONE}
                          </Space>
                        ),
                      },
                      {
                        title: "Test Date",
                        dataIndex: "testDate",
                        key: "testDate",
                        width: 200,
                        render: (value) => (value ? formatDateTime(value) : "-"),
                      },
                      {
                        title: "Notes",
                        dataIndex: "notes",
                        key: "notes",
                        ellipsis: true,
                        render: (value) =>
                          value ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {value.length > 90 ? `${value.slice(0, 90)}...` : value}
                            </Text>
                          ) : (
                            "-"
                          ),
                      },
                      {
                        title: "Action",
                        key: "actions",
                        width: 90,
                        align: "center",
                        render: (_, row) => (
                          <Dropdown
                            trigger={["click"]}
                            menu={getSubFeatureActions(feature, row.subFeature)}
                          >
                            <Button type="text" size="small" icon={<EllipsisOutlined />} />
                          </Dropdown>
                        ),
                      },
                    ]}
                    dataSource={detailTestCaseRows}
                    pagination={false}
                    rowKey="key"
                    size="small"
                    bordered
                    tableLayout="fixed"
                    scroll={{ x: 980 }}
                  />
                </Card>
              </Space>
            );
          })()
        ) : (
          <Empty description="No details to show" />
        )}
      </Drawer>

      <Drawer
        title={
          testCasesDrawerFeature
            ? `Test Cases: ${testCasesDrawerFeature.name}`
            : "Test Cases"
        }
        open={testCasesDrawerOpen}
        onClose={closeTestCasesDrawer}
        width="100vw"
        closable
        extra={
          testCasesDrawerFeature ? (
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAddTestCaseDrawer}
              >
                Add test case
              </Button>
              <Button onClick={closeTestCasesDrawer}>Close</Button>
            </Space>
          ) : (
            <Button onClick={closeTestCasesDrawer}>Close</Button>
          )
        }
        zIndex={99999}
        destroyOnClose
        styles={{
          wrapper: { maxWidth: "100vw" },
          body: { paddingBottom: 80 },
        }}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button onClick={closeTestCasesDrawer}>Close</Button>
          </div>
        }
      >
        <Space wrap style={{ marginBottom: 12 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search test case..."
            value={testCasesSearch}
            allowClear
            style={{ width: 320 }}
            onChange={(event) => {
              setTestCasesSearch(event.target.value);
              setTestCasesPagination((prev) => ({ ...prev, current: 1 }));
            }}
          />
          <Select
            value={testCasesResultFilter}
            style={{ width: 200 }}
            options={[
              { label: "All Results", value: "ALL" },
              { label: "Pass", value: "PASS" },
              { label: "Fail", value: "FAIL" },
              { label: "Untested", value: "NONE" },
            ]}
            onChange={(value) => {
              setTestCasesResultFilter(value || "ALL");
              setTestCasesPagination((prev) => ({ ...prev, current: 1 }));
            }}
          />
        </Space>

        <Table
          columns={testCasesDrawerColumns}
          dataSource={filteredTestCasesDrawerRows}
          rowKey="key"
          size="small"
          bordered
          tableLayout="fixed"
          pagination={{
            ...testCasesPagination,
            total: filteredTestCasesDrawerRows.length,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50],
            showTotal: (count, range) =>
              `${range[0]}-${range[1]} of ${count} test cases`,
          }}
          onChange={handleTestCasesDrawerTableChange}
          locale={{
            emptyText: (
              <Text type="secondary">
                {testCasesDrawerFeature
                  ? "No test cases found."
                  : "Select a feature to view test cases."}
              </Text>
            ),
          }}
          scroll={{ x: 920 }}
        />
      </Drawer>

      <Modal
        open={attachmentPreviewOpen}
        title={attachmentPreviewTitle}
        onCancel={() => setAttachmentPreviewOpen(false)}
        footer={null}
        width={attachmentPreviewIsImage ? 900 : 520}
        zIndex={MODAL_Z_INDEX}
        getContainer={() => document.body}
        destroyOnClose
      >
        {attachmentPreviewIsImage ? (
          <div style={{ textAlign: "center" }}>
            <Image
              src={attachmentPreviewUrl}
              alt={attachmentPreviewTitle}
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />
          </div>
        ) : attachmentPreviewUrl ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">Attachment</Text>
            <Button
              type="primary"
              href={attachmentPreviewUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open File
            </Button>
          </Space>
        ) : (
          <Text type="secondary">No preview available.</Text>
        )}
      </Modal>

      <Drawer
        title={
          testCasesDrawerFeature
            ? `Add Test Case: ${testCasesDrawerFeature.name}`
            : "Add Test Case"
        }
        open={addTestCaseDrawerOpen}
        onClose={closeAddTestCaseDrawer}
        width={420}
        zIndex={100000}
        destroyOnClose
      >
        <Form layout="vertical" form={addTestCaseForm} onFinish={saveNewTestCase}>
          <Form.Item
            name="testCase"
            label="Test Case"
            rules={[{ required: true, message: "Test case name is required" }]}
          >
            <Input placeholder="e.g., Ability to clone a Pm Task" maxLength={120} />
          </Form.Item>

          <Divider />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeAddTestCaseDrawer}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={addTestCaseSubmitting}
              disabled={!testCasesDrawerFeature}
            >
              Add
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={
          editTestCaseContext?.featureName
            ? `Rename Test Case: ${editTestCaseContext.featureName}`
            : "Rename Test Case"
        }
        open={editTestCaseDrawerOpen}
        onClose={closeEditTestCaseDrawer}
        width={420}
        zIndex={100010}
        destroyOnClose
      >
        <Form layout="vertical" form={editTestCaseForm} onFinish={renameTestCase}>
          <Form.Item
            name="testCase"
            label="Test Case"
            rules={[{ required: true, message: "Test case name is required" }]}
          >
            <Input placeholder="e.g., Ability to clone a Pm Task" maxLength={120} />
          </Form.Item>

          <Divider />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeEditTestCaseDrawer} disabled={editTestCaseSubmitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={editTestCaseSubmitting}
              disabled={!editTestCaseContext?.featureId}
            >
              Save
            </Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
}
